/* eslint-disable no-param-reassign */
import { action, thunk } from 'easy-peasy';
import gql from 'graphql-tag';
import swal from 'sweetalert';
import uuidv4 from 'uuid/v4';

import { toast } from 'react-toastify';

import {
  cognitoSignIn,
  cognitoConfirmOtp,
  cognitoSignOut,
} from '../utils/auth';
import client from '../utils/apolloClient';
import { updateMutation } from '../pages/UserManagement/Customer/Add';
import { requestPermission } from '../utils/firebase';

const getAuthenticationProfileQuery = gql`
  query getAuthenticationProfileQuery($input: AuthenticationProfileInput) {
    get_authentication_profile(input: $input) {
      cognito_username
      admin_auth_contact
      admin_auth_contact_verified
      is_service_account
      is_email_enabled
      error {
        code
        description
      }
      user_id
    }
  }
`;

export const getAuthenticationProfile = async ({ email }) => {
  try {
    const authenticationProfile = await client.clientPublic.query({
      query: getAuthenticationProfileQuery,
      variables: { input: { email } },
      fetchPolicy: 'network-only',
    });

    const data = authenticationProfile?.data?.get_authentication_profile;

    if (
      Array.isArray(data?.error) &&
      data.error[0] &&
      data.error[0].description
    ) {
      throw new Error(
        data.error[0].description || 'Authentication profile not found',
      );
    }

    if (!data) {
      throw new Error('Authentication profile not found');
    }

    const {
      admin_auth_contact,
      admin_auth_contact_verified,
      user_id,
      cognito_username,
      is_email_enabled,
      is_service_account,
    } = data;

    return {
      userId: user_id,
      phoneNumberVerified: admin_auth_contact_verified,
      phoneNumber: admin_auth_contact,
      cognitoUsername: cognito_username,
      isEmailEnabled: is_email_enabled,
      isServiceAccount: is_service_account,
    };
  } catch (err) {
    throw new Error(
      err?.message || 'Something went wrong while fetching auth profile',
    );
  }
};

const fetchUserQuery = gql`
  query fetch_user($input: NavigateUserInput) {
    fetch_user(input: $input) {
      status
      user_id
      first_name
      last_name
      display_name
      email
      color_code
      preferred_username
      role
      device_sessions {
        device_id
        expiry
      }
    }
  }
`;

const createUserLoginHistoryAPI = gql`
  mutation createUserLoginHistory($input: UserLoginHistoryInput) {
    create_user_login_history(input: $input) {
      user_id
    }
  }
`;

export const createUserLoginHistory = async ({ input }) => {
  try {
    const unique_id = uuidv4();
    fetch('https://api.ipify.org?format=json')
      .then(response => response.json())
      .then(async data => {
        const ipAddress = data.ip;
        await client.clientPrivate.mutate({
          mutation: createUserLoginHistoryAPI,
          variables: {
            input: {
              app_type: 'ADMIN_APP',
              platform: 'WEB',
              logged_in: true,
              user_type: 'KRAVEIN',
              login_type: 'DIRECT',
              web_browser: window?.navigator?.userAgent,
              ...input,
              ip_address: ipAddress,
              unique_id,
            },
          },
          client: client.clientPrivate,
        });
      })
      .catch(async error => {
        await client.clientPrivate.mutate({
          mutation: createUserLoginHistoryAPI,
          variables: {
            input: {
              app_type: 'ADMIN_APP',
              platform: 'WEB',
              logged_in: true,
              user_type: 'KRAVEIN',
              login_type: 'DIRECT',
              web_browser: window?.navigator?.userAgent,
              ...input,
              ip_address: 'ERROR RETRIEVING IP ADDRESS',
              unique_id,
            },
          },
          client: client.clientPrivate,
        });
        console.error('Error:', error);
      });
    window.localStorage.setItem('history_unique_id', unique_id);
  } catch (error) {
    console.log(error);
  }
};

const updateUserLoginHistoryAPI = gql`
  mutation updateUserLoginHistory($input: UserLoginHistoryInput) {
    update_user_login_history(input: $input) {
      user_id
    }
  }
`;

const updateUserLoginHistory = async ({ userId }) => {
  let unique_id = '';
  try {
    unique_id = window.localStorage.getItem('history_unique_id');
  } catch {
    // pass
  }

  try {
    window.localStorage.removeItem('history_unique_id');
  } catch {
    // pass
  }

  try {
    await client.clientPrivate.mutate({
      mutation: updateUserLoginHistoryAPI,
      variables: {
        input: {
          logged_in: false,
          user_type: 'KRAVEIN',
          user_id: userId,
          unique_id,
        },
      },
      client: client.clientPrivate,
    });
  } catch (error) {
    console.log(error);
  }
};

export default {
  menu: {
    defaultPlace: null,
    setDefaultPlace: action((state, payload) => {
      state.defaultPlace = payload;
    }),
    defaultPlaceId: null,
    setDefaultPlaceId: action((state, payload) => {
      state.defaultPlaceId = payload;
    }),
  },
  auth: {
    userId: null,
    user: {},
    userStatus: 'ACTIVE',
    isLoggedIn: false,
    authTokens: {},
    loading: false,
    isError: { message: '', error: false },
    otpRequired: false,
    redirecting: false,
    pendingUsername: null,
    pendingPassword: null,
    phoneVerified: false,
    cognitoUsername: null,
    authenticationProfile: {
      userId: null,
      phoneNumberVerified: null,
      phoneNumber: null,
      cognitoUsername: null,
      isEmailEnabled: false,
    },
    phoneVerificationCallback: null,
    allowedEmailOtp: false,
    otpAttempts: 0,
    rememberDevicePrompt: false,
    pendingDeviceSessions: [],

    setOtpAttempts: action((state, payload) => {
      state.otpAttempts = payload;
    }),

    setRememberDevicePrompt: action((state, payload) => {
      state.rememberDevicePrompt = payload;
    }),

    setPendingDeviceSessions: action((state, payload) => {
      state.pendingDeviceSessions = payload;
    }),

    setOtpRequired: action((state, payload) => {
      state.otpRequired = payload;
    }),

    setRedirecting: action((state, payload) => {
      state.redirecting = payload;
    }),

    setPendingUsername: action((state, payload) => {
      state.pendingUsername = payload;
    }),

    setPendingPassword: action((state, payload) => {
      state.pendingPassword = payload;
    }),

    setCognitoUsername: action((state, payload) => {
      state.cognitoUsername = payload;
    }),

    setPhoneVerified: action((state, payload) => {
      state.phoneVerified = payload;
    }),

    setAuthenticationProfile: action((state, payload) => {
      state.authenticationProfile = payload;
    }),

    setPhoneVerificationCallback: action((state, payload) => {
      state.phoneVerificationCallback = payload;
    }),

    setAuthTokens: action((state, payload) => {
      state.authTokens = payload;
      state.isLoggedIn = true;
      state.userId = payload.payload['custom:user_id'];
    }),

    setError: action((state, payload) => {
      state.isError = payload;
    }),

    clearError: action(state => {
      state.isError = { message: '', error: false };
    }),

    setLoading: action((state, payload) => {
      state.loading = payload;
    }),

    updateUser: action((state, payload) => {
      state.user = payload;
    }),

    logIn: thunk(async (actions, payload) => {
      const { username, password } = payload;
      try {
        actions.setError({ message: '', error: false });
        actions.setLoading(true);
        const storedDeviceId = window.localStorage.getItem('_tk');
        const storedExpiry = window.localStorage.getItem('_tkx');
        let effectiveDeviceId = '';
        if (storedDeviceId && storedExpiry) {
          if (Date.now() < parseInt(storedExpiry, 10)) {
            effectiveDeviceId = storedDeviceId;
          } else {
            window.localStorage.removeItem('_tk');
            window.localStorage.removeItem('_tkx');
          }
        }
        const data = await cognitoSignIn(username, password, {
          device_id: effectiveDeviceId,
        });
        if (data.status === 'CUSTOM_CHALLENGE') {
          actions.setPendingUsername(username);
          actions.setPendingPassword(password);
          actions.setOtpRequired(true);
          actions.setLoading(false);
          return true;
        }

        await actions.completeSignIn({
          data,
          username,
          history: payload.history,
        });

        return true;
      } catch (err) {
        console.log('cognitoSignIn error', err);
        const error = {
          message:
            err.message || 'Please Enter valid email address and password',
          error: true,
        };
        await actions.setError(error);
        await actions.setLoading(false);
        return false;
      }
    }),

    confirmOtp: thunk(async (actions, payload, { getState }) => {
      const { otp, history } = payload;
      const { pendingUsername, otpAttempts } = getState();
      try {
        actions.setError({ message: '', error: false });
        actions.setLoading(true);
        const storedDeviceId = window.localStorage.getItem('_tk');
        const data = await cognitoConfirmOtp(otp, {
          device_id: storedDeviceId || '',
        });
        actions.setOtpAttempts(0);
        actions.setOtpRequired(false);
        actions.setPendingUsername(null);
        actions.setPendingPassword(null);
        await actions.completeSignIn({
          data,
          username: pendingUsername,
          history,
          showRememberDevice: true,
        });
      } catch (err) {
        const isSessionExpired =
          err.message && err.message.toLowerCase().includes('invalid session');
        const isInvalidOtp = err.message === 'Invalid OTP. Please try again.';

        if (isSessionExpired) {
          cognitoSignOut();
          window.localStorage.removeItem('token');
          window.localStorage.removeItem('username');
          window.localStorage.removeItem('accessToken');
          window.localStorage.removeItem('refreshToken');
          actions.setOtpRequired(false);
          actions.setPendingUsername(null);
          actions.setPendingPassword(null);
          actions.setOtpAttempts(0);
          actions.setError({
            message: 'Your session has expired. Please sign in again.',
            error: true,
          });
        } else if (isInvalidOtp) {
          const newAttempts = otpAttempts + 1;

          if (newAttempts >= 3) {
            cognitoSignOut();
            window.localStorage.removeItem('token');
            window.localStorage.removeItem('username');
            window.localStorage.removeItem('accessToken');
            window.localStorage.removeItem('refreshToken');
            actions.setOtpRequired(false);
            actions.setPendingUsername(null);
            actions.setPendingPassword(null);
            actions.setOtpAttempts(0);
            actions.setError({
              message: 'Too many failed attempts. Please sign in again.',
              error: true,
            });
          } else {
            actions.setOtpAttempts(newAttempts);
            const remaining = 3 - newAttempts;
            actions.setError({
              message: `Invalid OTP. Please try again. ${remaining} attempt${
                remaining === 1 ? '' : 's'
              } remaining.`,
              error: true,
            });
          }
        } else {
          actions.setError({
            message: err.message || 'Something went wrong. Please try again.',
            error: true,
          });
        }

        actions.setRedirecting(false);
        actions.setLoading(false);
      }
    }),

    resendOtp: thunk(async (actions, _payload, { getState }) => {
      const { pendingUsername, pendingPassword } = getState();
      if (!pendingUsername || !pendingPassword) return false;
      try {
        actions.setError({ message: '', error: false });
        const storedDeviceId = window.localStorage.getItem('_tk');
        const data = await cognitoSignIn(pendingUsername, pendingPassword, {
          device_id: storedDeviceId || '',
        });
        if (data.status === 'CUSTOM_CHALLENGE') {
          actions.setOtpAttempts(0);
          actions.setOtpRequired(true);
          return true;
        }
        return false;
      } catch (err) {
        actions.setError({
          message: err.message || 'Failed to resend OTP. Please try again.',
          error: true,
        });
        return false;
      }
    }),

    completeSignIn: thunk(async (actions, payload) => {
      const { data, username, history, showRememberDevice } = payload;
      await window.localStorage.setItem('token', data.idToken);
      await window.localStorage.setItem('username', username);
      await window.localStorage.setItem('accessToken', data.accessToken);
      await window.localStorage.setItem('refreshToken', data.refreshToken);

      requestPermission(async token =>
        client.clientPrivate.mutate({
          mutation: updateMutation,
          variables: {
            input: {
              user_id: data.payload['custom:user_id'],
              device_token: {
                device_token: token,
                app_type: 'KRAVEIN',
                logged_in: true,
                platform: 'WEB',
              },
            },
          },
        }),
      );

      await client.clientPrivate
        .query({
          query: fetchUserQuery,
          variables: { input: { user_id: data.payload['custom:user_id'] } },
          fetchPolicy: 'network-only',
        })
        .then(async ({ data: result }) => {
          if (result.fetch_user && result.fetch_user.status === 'INACTIVE') {
            swal({ text: 'This user does not exist' }).then(async () => {
              await actions.logout();
              actions.setRedirecting(false);
              window.location.reload();
            });
          } else {
            const now = Date.now();
            const rawSessions = result.fetch_user.device_sessions || [];
            const cleanSessions = rawSessions.filter(
              s => s.expiry && parseInt(s.expiry, 10) > now,
            );

            if (cleanSessions.length < rawSessions.length) {
              try {
                await client.clientPrivate.mutate({
                  mutation: updateMutation,
                  variables: {
                    input: {
                      user_id: result.fetch_user.user_id,
                      device_sessions: cleanSessions.map(
                        ({ device_id, expiry }) => ({ device_id, expiry }),
                      ),
                    },
                  },
                });
              } catch (e) {
                console.log('device session cleanup failed', e);
              }
            }

            createUserLoginHistory({
              input: {
                email: result.fetch_user.email,
                first_name: result.fetch_user.first_name,
                last_name: result.fetch_user.last_name,
                user_id: result.fetch_user.user_id,
                unique_id: '',
                ip_address: '',
                build_number: '',
                device_name: '',
                model_number: '',
                version_number: '',
              },
            });
            actions.updateUser(result.fetch_user);
            await actions.setAuthTokens(data);
            await actions.setLoading(false);
            if (showRememberDevice) {
              actions.setPendingDeviceSessions(cleanSessions);
              actions.setRememberDevicePrompt(true);
            } else {
              history.push('/');
            }
          }
        });
    }),
    confirmTrustDevice: thunk(async (actions, payload, { getState }) => {
      const { history } = payload;
      const { userId, pendingDeviceSessions } = getState();
      const deviceId = uuidv4();
      const expiry = String(Date.now() + 30 * 24 * 60 * 60 * 1000);
      const updatedSessions = [
        ...pendingDeviceSessions.map(({ device_id, expiry: e }) => ({
          device_id,
          expiry: e,
        })),
        { device_id: deviceId, expiry },
      ];
      try {
        actions.setLoading(true);
        const result = await client.clientPrivate.mutate({
          mutation: updateMutation,
          variables: {
            input: { user_id: userId, device_sessions: updatedSessions },
          },
        });
        const errors = result.data?.update_user?.error;
        if (!errors || errors.length === 0) {
          window.localStorage.setItem('_tk', deviceId);
          window.localStorage.setItem('_tkx', expiry);
        }
      } catch (e) {
        console.log('confirmTrustDevice error', e);
      } finally {
        actions.setLoading(false);
        actions.setRememberDevicePrompt(false);
        actions.setPendingDeviceSessions([]);
        history.push('/');
      }
    }),

    declineTrustDevice: thunk(async (actions, payload) => {
      const { history } = payload;
      actions.setRememberDevicePrompt(false);
      actions.setPendingDeviceSessions([]);
      history.push('/');
    }),

    updateAuthenticationProfile: thunk(async (actions, payload) => {
      const { email, phoneNumber, userId, cognitoUsername } = payload;
      try {
        actions.setError({ message: '', error: false });
        actions.setLoading(true);
        const { data } = await client.clientPublic.mutate({
          mutation: gql`
            mutation updateAuthenticationProfile(
              $input: UpdateAuthenticationProfileInput
            ) {
              update_authentication_profile(input: $input) {
                message
                otp_verified
                contact {
                  type
                  value
                  display
                  is_primary
                }
                error {
                  description
                }
              }
            }
          `,
          variables: {
            input: {
              email,
              contact: [
                {
                  type: 'mobile',
                  value: phoneNumber,
                  display: true,
                  is_primary: true,
                },
              ],
              user_id: userId,
              cognito_username: cognitoUsername,
            },
          },
        });
        const err = data.update_authentication_profile?.error?.[0]?.description;
        if (err) {
          actions.setError({ message: err, error: true });
          actions.setLoading(false);
          return false;
        }
        actions.setLoading(false);
        return true;
      } catch (err) {
        actions.setError({
          message: err.message || 'Failed to update phone number.',
          error: true,
        });
        actions.setLoading(false);
        return false;
      }
    }),

    sendPhoneVerificationCode: thunk(async (actions, payload, { getState }) => {
      const { phoneNumber } = payload;
      const {
        authenticationProfile: { cognitoUsername, userId },
      } = getState();

      try {
        actions.setError({ message: '', error: false });
        actions.setLoading(true);
        const { data } = await client.clientPrivate.mutate({
          mutation: gql`
            mutation sendOtp($input: SendOtpInput) {
              send_otp(input: $input) {
                message
                otp_verified
                error {
                  description
                }
              }
            }
          `,
          variables: {
            input: {
              channel_type: 'SMS',
              phone_number: phoneNumber,
              user_id: userId,
              cognito_username: cognitoUsername,
            },
          },
        });
        const sendOtpError = data.send_otp?.error?.[0]?.description;
        if (sendOtpError) {
          toast.error(sendOtpError);
          actions.setLoading(false);
          return false;
        }
        actions.setLoading(false);
        return true;
      } catch (err) {
        actions.setError({
          message: err.message || 'Failed to send code.',
          error: true,
        });
        actions.setLoading(false);
        return false;
      }
    }),

    verifyPhone: thunk(async (actions, payload, { getState }) => {
      const { phoneNumber, code, history } = payload;
      const {
        authenticationProfile: { cognitoUsername, userId },
        allowedEmailOtp,
        phoneVerificationCallback,
      } = getState();

      try {
        actions.setError({ message: '', error: false });
        actions.setLoading(true);
        const { data } = await client.clientPrivate.mutate({
          mutation: gql`
            mutation verifyOtp($input: VerifyOtpInput) {
              verify_otp(input: $input) {
                message
                otp_verified
                error {
                  description
                }
              }
            }
          `,
          variables: {
            input: {
              channel_type: 'SMS',
              phone_number: phoneNumber,
              otp: code,
              user_id: userId,
              cognito_username: cognitoUsername,
            },
          },
        });
        if (data.verify_otp?.otp_verified) {
          actions.setPhoneVerified(true);
          actions.setLoading(false);
          if (allowedEmailOtp) {
            history.push('/');
          }

          phoneVerificationCallback();
        } else {
          const verifyOtpError = data.verify_otp?.error?.[0]?.description;
          toast.error(verifyOtpError || 'Invalid code. Please try again.');
          actions.setLoading(false);
        }
      } catch (err) {
        actions.setError({
          message: err.message || 'Invalid code. Please try again.',
          error: true,
        });
        actions.setLoading(false);
      }
    }),

    logout: action((state, payload) => {
      const { userId } = state;
      updateUserLoginHistory({ userId });
      state.user = null;
      state.loading = false;
      state.isError = { message: '', error: false };
      state.isLoggedIn = false;
      state.authTokens = {};
      state.token = null;
      state.userId = null;
      state.phoneVerified = false;
      state.pendingPassword = null;
      state.otpAttempts = 0;
      state.rememberDevicePrompt = false;
      state.pendingDeviceSessions = [];

      cognitoSignOut();
      window.localStorage.removeItem('token');
      window.localStorage.removeItem('username');
      window.localStorage.removeItem('accessToken');
      window.localStorage.removeItem('refreshToken');

      requestPermission(async token =>
        client.clientPrivate.mutate({
          mutation: updateMutation,
          variables: {
            input: {
              user_id: userId,
              device_token: {
                device_token: token,
                app_type: 'KRAVEIN',
                logged_in: false,
                platform: 'WEB',
              },
            },
          },
        }),
      );

      (payload ?? []).push('/login');
    }),
  },
};
